import {useUpmPlantTimezone} from '@hconnect/upm'
import {Box} from '@mui/material'
import {useDateScale} from '@widgets/common-d3-chart'
import {HCSlider, usePlanningChartStartEnd} from '@widgets/planning-schedule-chart'
import {throttle} from 'lodash'
import React, {FC, useCallback, useMemo} from 'react'

import type {NumberRange} from '../utils'

interface OperatorViewSliderProps {
  updateGanttCells: (range: NumberRange) => void
  cellWidth: number
}

export const OperatorViewSlider: FC<OperatorViewSliderProps> = React.memo(
  ({updateGanttCells, cellWidth}) => {
    const timezoneId = useUpmPlantTimezone()
    const {startOfPlan, endOfPlan, hoursRange, setHoursRange, stepsPerHour, totalSteps} =
      usePlanningChartStartEnd()

    const stepsPerDay = 24 * stepsPerHour
    const sliderMinSteps = stepsPerDay / 2

    const xScale = useDateScale({
      domain: [startOfPlan, endOfPlan],
      range: [0, cellWidth * totalSteps]
    })

    const throttledUpdateGanttCells = useMemo(
      () => throttle(updateGanttCells, 100),
      [updateGanttCells]
    )

    const convertStepToWidth = useCallback((step: number) => step * cellWidth, [cellWidth])

    return (
      <Box sx={{px: 3}}>
        <HCSlider
          timezone={timezoneId}
          convertStepToWidth={convertStepToWidth}
          xScale={xScale}
          label={{step: stepsPerDay}}
          minMax={[0, totalSteps]}
          stepsPerHour={stepsPerHour}
          roundClicks={{roundTo: 24 * stepsPerHour}}
          values={hoursRange}
          setValues={setHoursRange}
          stepLimits={{
            min: sliderMinSteps,
            max: totalSteps
          }}
          onChange={throttledUpdateGanttCells}
          onChangeComplete={updateGanttCells}
        />
      </Box>
    )
  }
)
